import { useCallback, useEffect, useState } from "react";
import { Field } from "react-final-form";

import Modal, { ModalSaveButton } from "../Modals/Modal";
import { Auth } from "../../api";
import Loading from "../Loading";
import { composeValidators, isEmail, required, strongPassword } from "./Shared";
import { useDispatch } from "react-redux";
import {
  setModalForgotPasswordFormOpen,
  setModalLoginFormOpen,
} from "../../actions/modals";
import axios from "../../api/axios";

const CUSTOM_QUESTION = "Custom question";

const shuffleArray = (array) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = array[i];
    array[i] = array[j];
    array[j] = temp;
  }
};

const errorMessages = {
  password: "Please enter a password",
  password_confirmation: "Please confirm your password",
  email: "Please enter your email address",
  secret_answer: "Please provide an answer to your security question",
  custom_question: "Please enter a custom question",
};

const validateErrors = (values) => {
  if (!values?.password) {
    return errorMessages.password;
  }
  const isStrong = strongPassword(values.password);

  if (isStrong) {
    return isStrong;
  }
  if (!values?.password_confirmation) {
    return errorMessages.password_confirmation;
  }

  if (values.password_confirmation !== values?.password) {
    return "Passwords don't match";
  }

  if (values.question === CUSTOM_QUESTION && !values.custom_question) {
    return errorMessages.custom_question;
  }

  if (!values.secret_answer) {
    return errorMessages.secret_answer;
  }

  return "";
};

export default function LoginOrSignUpModal({
  open,
  existingUser,
  handleClose,
  formApi,
  handleLogin,
  handleOpen,
}) {
  const dispatch = useDispatch();

  const [isNewUser, setIsNewUser] = useState(undefined);
  const [questions, setQuestions] = useState([]);
  const [type, setType] = useState("");
  const [error, setError] = useState("");

  const { change, getState } = formApi;

  useEffect(() => {
    const getSecurityQuestions = async () => {
      try {
        const questionsResponse = await axios.get("api/securityquestions");
        const questionOptions = questionsResponse.data.map((question) => ({
          value: question.question,
          label: question.question,
        }));
        shuffleArray(questionOptions);
        setQuestions([
          {
            value: "Please select a security question...",
            label: "Please select a security question...",
            disabled: true,
          },
          ...questionOptions,
          {
            value: "separator",
            label: "",
            disabled: true,
            role: "separator",
          },
          { value: CUSTOM_QUESTION, label: CUSTOM_QUESTION },
        ]);
        change("question", questionOptions[0].value);
      } catch (error) {
        console.log(error);
      }
    };
    if (isNewUser) {
      getSecurityQuestions();
    }
  }, [isNewUser, change]);

  useEffect(() => {
    const checkExisting = async () => {
      try {
        document.getElementById("user--auth-modal")?.focus();
        await Auth.uniqueEmail(existingUser.target.value);
        setIsNewUser(true);
        handleOpen();
        setTimeout(() => {
          document.getElementById("password")?.focus();
        }, 200);
      } catch (error) {
        if (error.message === "INVALID") {
          handleClose();
        } else {
          setIsNewUser(error.message !== "TAKEN");
          setTimeout(() => {
            document.getElementById("password")?.focus();
          }, 200);
          handleOpen();
        }
      }
    };
    if (open && existingUser?.target?.value && isNewUser === undefined) {
      checkExisting();
    }
  }, [open, existingUser, handleClose, isNewUser, handleOpen]);

  useEffect(() => {
    if (!open) {
      setIsNewUser(undefined);
      setError("");
    }
  }, [open]);

  const handleNoThanks = useCallback(() => {
    change("noThanks", true);
    change("signUp", false);
    handleClose();
  }, [handleClose, change]);

  const handleAddPassword = useCallback(() => {
    const {
      password_confirmation,
      password,
      question,
      custom_question,
      secret_answer,
    } = getState().values;

    const validationError = validateErrors({
      password,
      password_confirmation,
      question,
      custom_question,
      secret_answer,
    });
    if (validationError) {
      setError(validationError);
      return;
    }
    change("signUp", true);
    change("noThanks", false);
    handleClose();
  }, [handleClose, change, getState]);

  const handleTryLogin = useCallback(async () => {
    try {
      setType("saving");
      change("noThanks", false);
      change("signUp", false);
      const { email, password } = getState().values;
      if (!email || !password) {
        setError(errorMessages[!email ? "email" : "password"]);
        setType("");

        return;
      }
      await Auth.doLogin(email, password);
      const session = await Auth.getSession();
      setType("success");
      handleClose();
      handleLogin(session, getState().values);
    } catch (error) {
      setError(error?.message || "Login failed");
      setType("");
    }
  }, [change, getState, handleClose, handleLogin]);

  const handleForgotPassword = useCallback(() => {
    handleClose();
    setTimeout(() => {
      dispatch(setModalForgotPasswordFormOpen(getState().values.email));
      dispatch(setModalLoginFormOpen());
    }, 200);
  }, [dispatch, getState, handleClose]);

  if (!open) {
    return null;
  }

  return (
    <Modal isOpen modalName="edit-program" onClose={handleClose}>
      <div
        tabIndex="10"
        id="user--auth-modal"
        className="remodal remodal--help"
      >
        <div className="donate_signin_content">
          {isNewUser === undefined && (
            <div className="donate_signin_content-spinner">
              <Loading variant="inline" />
            </div>
          )}
          {isNewUser === true && (
            <div className="donate_signin_content-new_user">
              <div className="donate_signin_content-new_user-title">
                <h2>
                  Set up your secure password to activate your AFES account.
                </h2>
              </div>
              <div className="donate_signin_content-new_user-container">
                <div className="row">
                  <div className="col-12">
                    <Field
                      component="input"
                      type="password"
                      label=""
                      id="password"
                      name="password"
                      autoComplete="new-password"
                      placeholder="Password"
                      style={{ width: "-webkit-fill-available" }}
                      validate={composeValidators(required, strongPassword)}
                    />
                  </div>
                </div>
                <div className="row mt--12">
                  <div className="col-12">
                    <Field
                      component="input"
                      type="password"
                      label=""
                      id="password_confirmation"
                      name="password_confirmation"
                      autoComplete="new-password"
                      placeholder="Confirm Password"
                      style={{ width: "-webkit-fill-available" }}
                      validate={composeValidators(required)}
                    />
                  </div>
                </div>
                <div className="row mt--12">
                  <div className="col-12">
                    <Field
                      component="select"
                      label=""
                      id="question"
                      name="question"
                      placeholder="Choose question"
                      style={{ width: "-webkit-fill-available" }}
                      validate={composeValidators(required)}
                    >
                      {questions.map((question) => (
                        <option key={question.value} {...question} />
                      ))}
                    </Field>
                  </div>
                </div>
                {formApi.getState().values.question === CUSTOM_QUESTION && (
                  <div className="row mt--12">
                    <div className="col-12">
                      <Field
                        component="input"
                        label=""
                        id="custom_question"
                        name="custom_question"
                        placeholder="Custom Question"
                        style={{ width: "-webkit-fill-available" }}
                        validate={composeValidators(required)}
                      />
                    </div>
                  </div>
                )}
                <div className="row mt--12">
                  <div className="col-12">
                    <Field
                      component="input"
                      label=""
                      id="secret_answer"
                      name="secret_answer"
                      placeholder="Answer"
                      style={{ width: "-webkit-fill-available" }}
                      validate={composeValidators(required)}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    {!!error && <div className="form_error">{error}</div>}
                  </div>
                </div>
                <div className="row  mt--12">
                  <div
                    className="col-12"
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <ModalSaveButton onClick={handleAddPassword}>
                      Continue
                    </ModalSaveButton>
                    <button
                      onClick={handleNoThanks}
                      style={{
                        color: "#006cc5",
                        fontSize: "14px",
                      }}
                    >
                      Continue as guest
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {isNewUser === false && (
            <div className="donate_signin_content-new_user">
              <div className="donate_signin_content-new_user-title">
                <h2>Enter your password to prefill your personal details</h2>
              </div>
              <div className="donate_signin_content-new_user-container">
                <div className="row">
                  <div className="col-12">
                    <Field
                      component="input"
                      type="email"
                      label=""
                      id="email"
                      name="email"
                      autoComplete="email"
                      placeholder="john@doe.com"
                      className="flex-row__stretch "
                      disabled
                      style={{ width: "-webkit-fill-available" }}
                      validate={composeValidators(required, isEmail)}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-12 mt--12">
                    <Field
                      component="input"
                      type="password"
                      label=""
                      id="password"
                      name="password"
                      autoComplete="current-password"
                      placeholder="Password"
                      style={{ width: "-webkit-fill-available" }}
                      validate={composeValidators(required)}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-12">
                    {!!error && <div className="form_error">{error}</div>}
                  </div>
                </div>
                <div className="row  mt--12">
                  <div
                    className="col-12"
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <ModalSaveButton variant={type} onClick={handleTryLogin}>
                      Continue
                    </ModalSaveButton>
                    <button
                      onClick={handleForgotPassword}
                      style={{
                        color: "#006cc5",
                        fontSize: "14px",
                      }}
                    >
                      Forgot password
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
